import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { faChevronDown, faChevronUp } from '@fortawesome/pro-solid-svg-icons';
import classNames from 'classnames';
import type { FC } from 'react';
import { FormattedMessage, FormattedNumber, useIntl } from 'react-intl';
import styled from 'styled-components';

import { DivisionChange } from '@sorare/core/src/__generated__/globalTypes';
import { ButtonBase } from '@sorare/core/src/atoms/buttons/ButtonBase';
import { FontAwesomeIcon } from '@sorare/core/src/atoms/icons';
import { Collapse } from '@sorare/core/src/atoms/layout/Collapse';
import {
  Horizontal,
  SBHorizontal,
  Vertical,
} from '@sorare/core/src/atoms/layout/flex';
import {
  BrandS,
  BrandXS,
  BrandXXS,
  LabelL,
} from '@sorare/core/src/atoms/typography';
import { Ellipsis } from '@sorare/core/src/atoms/typography/Ellipsis';
import { Ordinal } from '@sorare/core/src/atoms/typography/Ordinal';
import { Bezel } from '@sorare/core/src/atoms/ui/bezels/Bezel';
import { TrophyIcon } from '@sorare/core/src/components/club/Trophies';
import { DialogKey } from '@sorare/core/src/components/navigation/WithDialogs';
import { Avatar } from '@sorare/core/src/components/user/Avatar';
import { useOpenDialog } from '@sorare/core/src/hooks/navigation/useOpenDialog';
import { useToggle } from '@sorare/core/src/hooks/useToggle';
import { glossary, playerDetails } from '@sorare/core/src/lib/glossary';
import { isType } from '@sorare/core/src/lib/gql';

import { CardsLeft } from 'components/leaderboard/CardsLeft';
import { ContenderBonus } from 'components/lineup/Contender/ContenderFooter/ContenderBonus';
import { DumbStatus } from 'components/managerTeam/Status/DumbStatus';

import { PodiumBar } from './PodiumBar';
import { ColoredRanking } from '../RankRewardTitle/ColoredRanking';
import type { RankRow_userRankingInterface } from './__generated__/index.graphql';

const BrandSup = styled(BrandXXS).attrs({ as: 'sup', bold: true })``;
const Root = styled(Horizontal)`
  position: relative;
  width: 100%;
`;
const StyledButtonBase = styled(ButtonBase)`
  text-align: left;
`;
const StyledBezel = styled(Bezel)`
  --podium-color: var(--c-nd-100);

  position: relative;
  padding: var(--intermediate-unit);
  flex: 1;

  background: linear-gradient(
      199deg,
      var(--podium-color) -40%,
      var(--c-black) 20%
    ),
    var(--c-black);

  &.rank-1 {
    --podium-color: #fcff63;
  }
  &.rank-2 {
    --podium-color: #99bdc8;
  }
  &.rank-3 {
    --podium-color: #ffa463;
  }
  &.isMe {
    --podium-color: var(--c-white);
    background: linear-gradient(
        199deg,
        var(--podium-color) -40%,
        var(--c-nd-100) 20%
      ),
      var(--c-black);
  }
  &.isSelected {
    --podium-color: var(--c-blue-400);
  }
`;

const AvatarContainer = styled(Horizontal).attrs({ gap: 0, center: true })`
  position: relative;
  border-radius: var(--quadruple-unit);
  background-color: var(--c-nd-400);

  & > img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const StatusContainer = styled.div`
  position: absolute;
  display: flex;
  left: 0;
  top: 0;
  transform: translate(-3px, -3px);
`;

type Props = {
  ranking: RankRow_userRankingInterface;
  isMe?: boolean;
  isSelected?: boolean;
  startOpen?: boolean;
  collapsible?: boolean;
  children?: FC<React.PropsWithChildren<{ expanded: boolean }>>;
  hideRank?: boolean;
  onClick?: () => void;
};

export const RankRow = ({
  isMe,
  ranking,
  isSelected,
  children,
  startOpen = false,
  collapsible = true,
  hideRank = false,
  onClick,
}: Props) => {
  const { formatMessage } = useIntl();
  const openDialog = useOpenDialog();
  const [expanded, toggleExpanded] = useToggle(startOpen);

  const { overallScore, overallRanking, user } = ranking;

  const so5Lineup = isType(ranking, 'So5Ranking')
    ? ranking.so5Lineup
    : undefined;

  const lastSo5Lineup = isType(ranking, 'So5Ranking')
    ? ranking.so5Lineup
    : ranking.lastSo5Rankings?.[0]?.so5Lineup;

  const onClickCallback = () => {
    if (children && collapsible) {
      return toggleExpanded;
    }
    if (onClick) {
      return onClick;
    }
    if (isType(ranking, 'So5Ranking')) {
      return () =>
        openDialog(
          DialogKey.contender,
          ranking.so5Lineup.so5LeaderboardContender.slug
        );
    }
    return undefined;
  };

  const content = (
    <Root>
      <PodiumBar rank={overallRanking} isMe={isMe} />
      <StyledBezel
        className={classNames(`rank-${overallRanking}`, { isMe, isSelected })}
        borderColor={
          isSelected
            ? 'var(--c-blue-400)'
            : 'linear-gradient(199deg, var(--podium-color), var(--c-nd-100) 20%), var(--c-nd-100)'
        }
      >
        <Vertical gap={expanded ? 2 : 0}>
          <SBHorizontal>
            <Horizontal>
              <AvatarContainer>
                {isType(ranking, 'So5Ranking') &&
                  !!ranking.eligibleDivisionChange &&
                  ranking.eligibleDivisionChange !== DivisionChange.STABLE && (
                    <StatusContainer>
                      <DumbStatus
                        divisionChange={ranking.eligibleDivisionChange}
                        size={2}
                      />
                    </StatusContainer>
                  )}
                <Avatar user={user} rounded size={5} />
              </AvatarContainer>
              <Vertical gap={0.5}>
                <LabelL bold>
                  <Ellipsis>{user.nickname}</Ellipsis>
                </LabelL>
                {so5Lineup?.cancelledAt ? (
                  <BrandXS color="var(--c-yellow-400)">
                    <FormattedMessage
                      id="RankRewards.Ranking.canceled"
                      defaultMessage="Canceled lineup"
                    />
                  </BrandXS>
                ) : (
                  <Horizontal>
                    <Horizontal gap={0.5}>
                      <BrandS as="span" color="var(--c-white)">
                        <FormattedNumber value={overallScore} />
                      </BrandS>
                      <BrandXXS color="var(--c-nd-600)" as="span">
                        <FormattedMessage {...playerDetails.points} />
                      </BrandXXS>
                    </Horizontal>
                    {!!so5Lineup?.bonusPoints && (
                      <ContenderBonus
                        bonusRating={so5Lineup.bonusRating}
                        bonus={so5Lineup.bonusPoints}
                      />
                    )}
                  </Horizontal>
                )}
              </Vertical>
            </Horizontal>
            <Horizontal>
              {!!lastSo5Lineup && <CardsLeft so5Lineup={lastSo5Lineup} />}
              {!!overallRanking && !hideRank && (
                <>
                  <TrophyIcon ranking={overallRanking} />
                  <ColoredRanking className={`rank-${overallRanking}`}>
                    <Ordinal ordinal={overallRanking} Sup={BrandSup} />
                  </ColoredRanking>
                </>
              )}
              {!!children && !!collapsible && (
                <FontAwesomeIcon
                  icon={expanded ? faChevronUp : faChevronDown}
                  height={16}
                  aria-label={formatMessage(
                    expanded ? glossary.showLess : glossary.showMore
                  )}
                />
              )}
            </Horizontal>
          </SBHorizontal>
          <Collapse open={expanded} gap={0}>
            {children?.({ expanded })}
          </Collapse>
        </Vertical>
      </StyledBezel>
    </Root>
  );

  if (onClickCallback) {
    return (
      <StyledButtonBase onClick={onClickCallback()}>{content}</StyledButtonBase>
    );
  }
  return content;
};

RankRow.fragments = {
  userRankingInterface: gql`
    fragment RankRow_userRankingInterface on UserRankingInterface {
      id
      overallRanking
      overallScore
      user {
        slug
        nickname
        ...Avatar_publicUserInfoInterface
      }
      ... on So5Ranking {
        id
        eligibleDivisionChange
        so5Lineup {
          id
          so5LeaderboardContender {
            slug
          }
          bonusPoints
          bonusRating
          cancelledAt
          ...CardsLeft_so5Lineup
        }
      }
      ... on So5UserGroupMembership {
        id
        lastSo5Rankings(size: 1) {
          id
          so5Lineup {
            id
            ...CardsLeft_so5Lineup
          }
        }
      }
    }
    ${CardsLeft.fragments.so5Lineup}
    ${Avatar.fragments.publicUserInfoInterface}
  ` as TypedDocumentNode<RankRow_userRankingInterface>,
};
